import { send } from '@actual-app/core/platform/client/connection';
import {
  parseNumberFormat,
  setNumberFormat,
} from '@actual-app/core/shared/util';
import type {
  GlobalPrefs,
  MetadataPrefs,
  ServerPrefs,
  SyncedPrefs,
} from '@actual-app/core/types/prefs';
import { createSlice } from '@reduxjs/toolkit';
import type { PayloadAction } from '@reduxjs/toolkit';

import { resetApp } from '#app/appSlice';
import { DEFAULT_LANGUAGE, setI18NextLanguage } from '#i18n';
import { closeModal } from '#modals/modalsSlice';
import { createAppAsyncThunk } from '#redux';
import { getUserData } from '#users/usersSlice';

import { conFormatosRegionales } from './formatosRegionales';

const sliceName = 'prefs';

type PrefsState = {
  local: MetadataPrefs;
  global: GlobalPrefs;
  synced: SyncedPrefs;
  server: ServerPrefs;
};

const initialState: PrefsState = {
  local: {},
  global: {},
  synced: {},
  server: {},
};

export const loadPrefs = createAppAsyncThunk(
  `${sliceName}/loadPrefs`,
  async (_, { dispatch, getState }) => {
    const prefs = await send('load-prefs');

    // Remove any modal state if switching between budgets
    const currentPrefs = getState().prefs.local;
    if (prefs && prefs.id && !currentPrefs) {
      dispatch(closeModal());
    }

    const [globalPrefs, syncedPrefsGuardadas] = await Promise.all([
      send('load-global-prefs'),
      send('preferences/get'),
    ]);

    // H09: en castellano/catalán, fecha dd/MM/yyyy, semana en lunes y 1.000,33
    // salvo que el usuario ya haya elegido otra cosa.
    const syncedPrefs = conFormatosRegionales(
      syncedPrefsGuardadas,
      globalPrefs.language || DEFAULT_LANGUAGE,
    );

    dispatch(
      setPrefs({ local: prefs, global: globalPrefs, synced: syncedPrefs }),
    );

    // Certain loot-core utils depend on state outside of the React tree, update them
    setNumberFormat(
      parseNumberFormat({
        format: syncedPrefs.numberFormat,
        hideFraction: syncedPrefs.hideFraction,
      }),
    );

    // We need to load translations before the app renders
    setI18NextLanguage(globalPrefs.language ?? '');

    return prefs;
  },
);

type SavePrefsPayload = {
  prefs: MetadataPrefs;
};

export const savePrefs = createAppAsyncThunk(
  `${sliceName}/savePrefs`,
  async ({ prefs }: SavePrefsPayload, { dispatch }) => {
    await send('save-prefs', prefs);
    dispatch(mergeLocalPrefs(prefs));
  },
);

export const loadGlobalPrefs = createAppAsyncThunk(
  `${sliceName}/loadGlobalPrefs`,
  async (_, { dispatch, getState }) => {
    const globalPrefs = await send('load-global-prefs');
    dispatch(
      setPrefs({
        local: getState().prefs.local,
        global: globalPrefs,
        synced: getState().prefs.synced,
      }),
    );
    return globalPrefs;
  },
);

type SaveGlobalPrefsPayload = {
  prefs: GlobalPrefs;
  onSaveGlobalPrefs?: () => void;
};

export const saveGlobalPrefs = createAppAsyncThunk(
  `${sliceName}/saveGlobalPrefs`,
  async (
    { prefs, onSaveGlobalPrefs }: SaveGlobalPrefsPayload,
    { dispatch },
  ) => {
    await send('save-global-prefs', prefs);
    dispatch(mergeGlobalPrefs(prefs));
    onSaveGlobalPrefs?.();
  },
);

type SaveSyncedPrefsPayload = {
  prefs: SyncedPrefs;
};

export const saveSyncedPrefs = createAppAsyncThunk(
  `${sliceName}/saveSyncedPrefs`,
  async ({ prefs }: SaveSyncedPrefsPayload, { dispatch }) => {
    await Promise.all(
      Object.entries(prefs).map(([prefName, value]) =>
        send('preferences/save', {
          id: prefName as keyof SyncedPrefs,
          value,
        }),
      ),
    );
    dispatch(mergeSyncedPrefs(prefs));
  },
);

/**
 * Rumbo · Travesías: vuelve a leer de la base de datos solo las claves
 * `recuerdo:*` y fusiona las que cambian (las de la pareja llegan por
 * sincronización y `state.prefs.synced` no se entera). Lo que la interfaz
 * fusionó y la base de datos no tiene («Solo ver») se quita.
 */
export const recargarRecuerdos = createAppAsyncThunk(
  `${sliceName}/recargarRecuerdos`,
  async (_, { dispatch, getState }) => {
    const guardadas = await send('preferences/get');
    const actuales = getState().prefs.synced;
    const cambios: SyncedPrefs = {};
    for (const [clave, valor] of Object.entries(guardadas)) {
      const k = clave as keyof SyncedPrefs;
      if (clave.startsWith('recuerdo:') && actuales[k] !== valor) {
        cambios[k] = valor;
      }
    }
    for (const clave of Object.keys(actuales)) {
      const k = clave as keyof SyncedPrefs;
      if (clave.startsWith('recuerdo:') && !(k in guardadas)) {
        cambios[k] = undefined;
      }
    }
    if (Object.keys(cambios).length > 0) {
      dispatch(mergeSyncedPrefs(cambios));
    }
  },
);

type SaveServerPrefsPayload = {
  prefs: ServerPrefs;
};

export const saveServerPrefs = createAppAsyncThunk(
  `${sliceName}/saveServerPrefs`,
  async ({ prefs }: SaveServerPrefsPayload, { dispatch }) => {
    const result = await send('save-server-prefs', { prefs });
    if (result && 'error' in result) {
      return { error: result.error };
    }

    dispatch(mergeServerPrefs(prefs));
    return {};
  },
);

type SetPrefsPayload = {
  local: MetadataPrefs;
  global: GlobalPrefs;
  synced: SyncedPrefs;
};

type MergeLocalPrefsPayload = MetadataPrefs;
type MergeGlobalPrefsPayload = GlobalPrefs;
type MergeSyncedPrefsPayload = SyncedPrefs;
type MergeServerPrefsPayload = ServerPrefs;

const prefsSlice = createSlice({
  name: sliceName,
  initialState,
  reducers: {
    setPrefs(state, action: PayloadAction<SetPrefsPayload>) {
      state.local = action.payload.local;
      state.global = action.payload.global;
      state.synced = action.payload.synced;
    },
    mergeLocalPrefs(state, action: PayloadAction<MergeLocalPrefsPayload>) {
      state.local = { ...state.local, ...action.payload };
    },
    mergeGlobalPrefs(state, action: PayloadAction<MergeGlobalPrefsPayload>) {
      state.global = { ...state.global, ...action.payload };
    },
    mergeSyncedPrefs(state, action: PayloadAction<MergeSyncedPrefsPayload>) {
      state.synced = { ...state.synced, ...action.payload };
    },
    mergeServerPrefs(state, action: PayloadAction<MergeServerPrefsPayload>) {
      state.server = { ...state.server, ...action.payload };
    },
  },
  extraReducers: builder => {
    builder.addCase(resetApp, state => ({
      ...initialState,
      global: state.global || initialState.global,
      server: state.server || initialState.server,
    }));
    builder.addCase(getUserData.fulfilled, (state, action) => {
      if (!action.payload || typeof action.payload !== 'object') {
        return state;
      }

      const { serverPrefs } = action.payload as {
        serverPrefs?: ServerPrefs | null;
      };

      if (!serverPrefs) {
        return state;
      }

      state.server = {
        ...state.server,
        ...serverPrefs,
      };
    });
  },
});

export const { name, reducer, getInitialState } = prefsSlice;

export const actions = {
  ...prefsSlice.actions,
  loadPrefs,
  savePrefs,
  loadGlobalPrefs,
  saveGlobalPrefs,
  saveSyncedPrefs,
  saveServerPrefs,
  recargarRecuerdos,
};

export const {
  mergeGlobalPrefs,
  mergeLocalPrefs,
  mergeServerPrefs,
  mergeSyncedPrefs,
  setPrefs,
} = actions;
